import { supabase } from './supabase';

interface ReportSubmissionData {
    objectDesc: string;
    sceneDesc: string;
    severity: string;
    latitude?: number;
    longitude?: number;
    municipality?: string | null;
    imageFile: File | Blob;
    userId?: string;
    category?: string;
    address?: string;
    phone?: string;
    reporterName?: string;
    honeypot?: string; // Bot protection
    is_anonymous?: boolean;
    authority_id?: string;
}

export const submitReport = async (data: ReportSubmissionData) => {
    // Client-side honeypot check (fast path — blocks obvious bots before any network call)
    if (data.honeypot) {
        return { id: 'bot-blocked', imageUrl: '' };
    }

    // SECURITY: Pre-check rate limit BEFORE uploading image to storage.
    // This prevents bots from exhausting storage quota even when the submission
    // is later rejected by the Edge Function rate limiter.
    const { data: preCheck, error: preCheckError } = await supabase.rpc('check_rate_limit', {
        p_ip: 'client', // Server will use real IP — this is just a client-side pre-check
        p_action: 'report_submission',
        p_max_attempts: 20,
        p_window_interval: '1 hour'
    });

    // If the pre-check explicitly says blocked (and didn't error), stop here.
    if (!preCheckError && preCheck && !preCheck.allowed) {
        throw new Error('For mange rapporter. Vennligst vent litt før du prøver igjen.');
    }

    // Safe fallback for UUID generation
    const generateSafeId = () => {
        if (typeof crypto !== 'undefined' && crypto.randomUUID) {
            return crypto.randomUUID();
        }
        return Date.now().toString(36) + Math.random().toString(36).substring(2, 15);
    };

    const fileName = `${generateSafeId()}.webp`;

    // 1. Upload image to storage (only after pre-check passes)
    const { error: uploadError } = await supabase.storage
        .from('reports')
        .upload(fileName, data.imageFile);

    if (uploadError) throw uploadError;

    // 2. Get public URL
    const { data: { publicUrl } } = supabase.storage
        .from('reports')
        .getPublicUrl(fileName);

    // 3. Call Edge Function for final submission (authoritative server-side rate limiting)
    const { data: report, error: functionError } = await supabase.functions.invoke('submit-report', {
        body: {
            objectDesc: data.objectDesc,
            sceneDesc: data.sceneDesc,
            severity: data.severity,
            latitude: data.latitude,
            longitude: data.longitude,
            municipality: data.municipality,
            address: data.address,
            imagePath: publicUrl,
            userId: data.userId,
            category: data.category,
            phone: data.phone,
            reporterName: data.reporterName,
            honeypot: data.honeypot,
            is_anonymous: data.is_anonymous,
            authority_id: data.authority_id
        }
    });

    if (functionError) {
        // Cleanup uploaded image since report was rejected
        await supabase.storage.from('reports').remove([fileName]);

        let errorMessage = 'Ukjent feil ved sending av rapport.';

        // Supabase FunctionInvokeError carries a non-standard `context` with the raw Response.
        // Cast to unknown first to safely access it without suppressing type-checking.
        const invokeError = functionError as unknown as { context?: Response };
        if (invokeError.context?.json) {
            const body = await invokeError.context.json() as { error?: string; details?: string | string[] };
            if (body.error && body.details) {
                errorMessage = Array.isArray(body.details)
                    ? `${body.error}: ${body.details.join(', ')}`
                    : `${body.error}: ${body.details}`;
            } else if (body.error) {
                errorMessage = body.error;
            }
        } else if (functionError instanceof Error) {
            errorMessage = functionError.message;
        }

        if (functionError.status === 429) {
            throw new Error('For mange rapporter. Vennligst vent litt før du prøver igjen.');
        }
        if (functionError.status === 400) {
            throw new Error(`Valideringsfeil: ${errorMessage}`);
        }
        throw new Error(`Kunne ikke sende rapport (${functionError.status || '??'}): ${errorMessage}`);
    }

    return { id: report.id, case_id: report.case_id, imageUrl: publicUrl };

};


const LIST_VIEW_COLUMNS = 'id, case_id, description, created_at, status, municipality, image_url, latitude, longitude, user_id, category, severity, resolution_code';

export const fetchRecentReports = async (limit = 10, page = 0) => {
    const from = page * limit;
    const to = from + limit - 1;

    const { data, error } = await supabase
        .from('reports')
        .select(LIST_VIEW_COLUMNS)
        .order('created_at', { ascending: false })
        .range(from, to);

    if (error) throw error;
    return data;
};

export const fetchUserReports = async (userId: string, limit = 20, page = 0) => {
    const from = page * limit;
    const to = from + limit - 1;

    const { data, error } = await supabase
        .from('reports')
        .select(LIST_VIEW_COLUMNS)
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .range(from, to);

    if (error) throw error;
    return data || [];
};

export const fetchCommunityReports = async (limit = 20, page = 0) => {
    const offset = page * limit;

    const { data, error } = await supabase.rpc('get_public_reports', {
        p_limit: limit,
        p_offset: offset
    });

    if (error) throw error;
    return data;
};

export const fetchMapReports = async () => {
    const { data, error } = await supabase.rpc('get_map_reports');
    if (error) throw error;
    return data;
};

export const deleteUserReports = async (userId: string) => {
    // 1. Fetch reports to find image URLs
    const { data: reports, error: fetchError } = await supabase
        .from('reports')
        .select('image_url')
        .eq('user_id', userId);

    if (fetchError) throw fetchError;

    // 2. Delete images from storage
    if (reports && reports.length > 0) {
        const fileNames = reports.map(r => {
            const parts = r.image_url.split('/');
            return parts[parts.length - 1];
        });

        const { error: storageError } = await supabase.storage
            .from('reports')
            .remove(fileNames);

        if (storageError) console.warn('Some images could not be deleted:', storageError);
    }

    // 3. Delete reports from DB
    const { error: dbError } = await supabase
        .from('reports')
        .delete()
        .eq('user_id', userId);

    if (dbError) throw dbError;
};

export const deleteUserAccount = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    // Delete reports first
    await deleteUserReports(user.id);

    // Profile delete is handled by the cascaded auth.users delete (if set up)
    // or we can manually delete it from public.profiles
    const { error: profileError } = await supabase
        .from('profiles')
        .delete()
        .eq('id', user.id);

    if (profileError) throw profileError;

    // This handles the auth side. Note: User must be signed in.
    // In production, you might want a backend function for this.
    const { error: authError } = await supabase.rpc('delete_own_user');

    if (authError) {
        console.error('Auth deletion failed:', authError);
        // Fallback: just sign out if RPC fails (needs to be implemented in DB)
        await supabase.auth.signOut();
    }
};

export const fetchReportUpdates = async (reportId: string, includeInternal = false) => {
    let query = supabase
        .from('report_updates')
        .select('*, profiles:created_by (full_name, role, authority_id, authorities(name))')
        .eq('report_id', reportId)
        .order('created_at', { ascending: true });

    if (!includeInternal) {
        query = query.eq('is_internal', false);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data;
};

import { ReportStatus, AuditActionType, formatAuditComment, ResolutionCode } from './workflowTypes';

export const addReportUpdate = async (
    reportId: string,
    status: ReportStatus,
    comment: string,
    isInternal: boolean,
    auditAction: AuditActionType = 'INFO'
) => {
    const { data: { user } } = await supabase.auth.getUser();

    // Enforce semantic logging
    const semanticComment = formatAuditComment({
        action: auditAction,
        details: comment
    });

    const { data, error } = await supabase.from('report_updates').insert({
        report_id: reportId,
        status,
        comment: semanticComment,
        is_internal: isInternal,
        created_by: user?.id
    }).select().single();

    if (error) throw error;
    return data;
};

export const updateReportStatus = async (
    reportId: string,
    newStatus: ReportStatus,
    comment: string,
    resolutionCode?: ResolutionCode
) => {
    // 1. Fetch current status (optional, but good for strict validation if not passed in)
    // For now we trust the transition call, but we apply logic gates

    // 2. Validate closing logic
    if (newStatus === 'RESOLVED' && !resolutionCode) {
        throw new Error('Resolution code is mandatory when closing a case.');
    }

    // 3. Format comment with resolution code if needed
    let finalComment = comment;
    const auditAction: AuditActionType = newStatus === 'RESOLVED' ? 'DECISION' : 'STATUS';

    // 4. Update Report Table
    const { error } = await supabase
        .from('reports')
        .update({
            status: newStatus,
            resolved_at: newStatus === 'RESOLVED' ? new Date().toISOString() : null,
            resolution_code: newStatus === 'RESOLVED' ? resolutionCode : null
        })
        .eq('id', reportId);

    if (error) throw error;

    // 5. Add Audit Log
    await addReportUpdate(
        reportId,
        newStatus,
        finalComment,
        false, // Status updates are generally public unless specified otherwise
        auditAction
    );

    return true;
};

export const updateReportFeedback = async (reportId: string, rating: number, comment: string) => {
    const { error } = await supabase
        .from('reports')
        .update({
            rating,
            feedback_comment: comment
        })
        .eq('id', reportId);

    if (error) throw error;
};

export const transferReport = async (reportId: string, newAuthorityId: string, reason: string) => {
    // 1. Update the report's authority_id
    const { error: updateError } = await supabase
        .from('reports')
        .update({
            authority_id: newAuthorityId,
            status: 'IN_PROGRESS'
        })
        .eq('id', reportId);

    if (updateError) throw updateError;

    // 2. Add an internal log entry
    await addReportUpdate(
        reportId,
        'IN_PROGRESS',
        `Case transferred to new authority. Reason: ${reason}`,
        true,
        'ASSIGNMENT'
    );

    return true;
};

// -- SECURE METHODS --

export const searchReportByCaseNumber = async (caseNumber: string) => {
    const { data, error } = await supabase
        .rpc('api_search_case_number', { p_case_number: caseNumber });

    if (error) throw error;
    if (data && data.length > 0) return data[0]; // Returns { id: uuid }
    return null;
};

/**
 * Fetches report details securely.
 * Uses Direct Select first (fastest, standard RLS).
 * If RLS blocks it (returns null), it falls back to Secure RPC (Guest Mode).
 */
export const fetchReportSecurely = async (reportId: string) => {
    // 1. Try Direct Select first (Standard Path)
    const { data: directData, error: directError } = await supabase
        .from('reports')
        .select('*, authorities(name), assigned_user:profiles!reports_assigned_to_fkey(full_name, email)')
        .eq('id', reportId)
        .maybeSingle(); // Use maybeSingle to avoid 406 error if blocked by RLS

    // If we have data, RLS allowed it. Return it.
    if (directData) return directData;

    // If error is NOT an RLS block (e.g. network), throw it.
    // Note: Supabase RLS returns empty data, not an error code usually, unless using .single()
    if (directError) {
        console.error('Direct fetch error:', directError);
        throw directError;
    }

    // 2. Fallback to RPC (Guest/Redacted Path)
    if (import.meta.env.DEV) console.log('[SECURITY] Direct fetch blocked/empty. Trying RPC...');
    const { data: rpcData, error: rpcError } = await supabase
        .rpc('api_get_case_details', { p_id: reportId });

    if (rpcError) throw rpcError;
    return rpcData;
};
